import { mapActions } from "vuex";
import {
  UnpublishInvoiceProxy,
  CaSignProxy,
  PublishedInvoiceProxy,
  EInvoiceProxy
} from "@/store/proxies/e-invoice";
import { ERROR_CODES, DOWNLOAD_URL } from "@/app-constant";
import PTokenProxy from "@/store/proxies/p-token-proxy";
import { resolveReleaseErrorPayload } from "@/utils/release-invoice-error-display";

const getCurrentUser = () => {
  try {
    return JSON.parse(localStorage.getItem("currentUser")) || {};
  } catch (e) {
    return {};
  }
};

/** MST hoặc CMND trên subject chứng thư (khớp MST công ty thì không cần so seri). */
const usbTaxOrIdFromSubject = usbInfo => {
  if (!usbInfo || !usbInfo.subject) return "";
  const s = usbInfo.subject;
  let m = s.match(/MST:\s*([0-9]{10,14})/i);
  if (m) return m[1].trim();
  m = s.match(/CMND:\s*([0-9]+)/i);
  return m ? m[1].trim() : "";
};

const getRegisteredSerials = () =>
  new EInvoiceProxy()
    .getListSetting()
    .then(res => {
      const raw = res && res.list_seri_cks;
      return Array.isArray(raw) ? raw : [];
    })
    .catch(() => []);

const hasHsm = caResponse =>
  caResponse &&
  caResponse.length > 0 &&
  caResponse[0].ca_type === "HSM";

/**
 * So sánh seri USB (X) với list_seri_cks (Y). Theo spec: Y rỗng hoặc X rỗng → pass;
 * còn lại: X ∈ Y mới pass. Serial chỉ lấy từ usbInfo.seri (Ptoken /current-usb-info).
 */
const validateUsb = usbInfo => {
  const company = getCurrentUser().company || {};
  const companyTax =
    (company.tax_code && String(company.tax_code).trim()) || "";
  const usbId = usbTaxOrIdFromSubject(usbInfo);

  console.log("[USB Validate]", {
    usbSubject: usbInfo && usbInfo.subject,
    usbId,
    companyTaxCode: companyTax,
    isTaxMatch: !!(usbId && companyTax && usbId === companyTax)
  });

  if (usbId && companyTax && usbId === companyTax) {
    return Promise.resolve({ valid: true });
  }

  const seriRaw =
    usbInfo && usbInfo.seri != null ? String(usbInfo.seri).trim() : "";
  const seriNorm = seriRaw.toLowerCase();

  if (!seriNorm) {
    console.log("[USB Validate]", { passReason: "empty_seri" });
    return Promise.resolve({ valid: true });
  }

  return getRegisteredSerials().then(list => {
    if (!list.length) {
      console.log("[USB Validate]", { passReason: "empty_list_seri_cks" });
      return { valid: true };
    }
    const ok = list.some(
      s => s && String(s).trim().toLowerCase() === seriNorm
    );
    console.log("[USB Validate]", {
      seri: seriNorm,
      list_seri_cks: list,
      isSerialMatch: ok
    });
    return ok ? { valid: true } : { valid: false, error: ERROR_CODES.TAX_CODE_ERROR };
  });
};

const getCurrentUsbInfo = () => {
  const proxy = new PTokenProxy();
  return new Promise((resolve, reject) => {
    proxy.testConnectionUsb().then(
      testUsbRes => {
        if (!testUsbRes) {
          reject(ERROR_CODES.USB_NOT_ACCESSIBLE);
          return;
        }

        proxy
          .currentUsbInfo()
          .then(usbInfo => validateUsb(usbInfo))
          .then(({ valid, error }) => (valid ? resolve(true) : reject(error)))
          .catch(() => {
            reject(ERROR_CODES.USB_NOT_ACCESSIBLE);
          });
      },
      err => {
        let errMsg = ERROR_CODES.APP_NOT_START;
        if (err && err !== "Network Error") {
          errMsg =
            !!err.message &&
            err.message.includes("Vui lòng cắm USB và đăng nhập vào APP Ptoken")
              ? ERROR_CODES.USB_NOT_CONNECT
              : ERROR_CODES.APP_NOT_UP_TO_DATE;
        }
        reject(errMsg);
      }
    );
  });
};

const ReleaseInvoiceMixin = {
  name: "ReleaseInvoiceMixin",
  data() {
    return {
      LICENSE_EXPIRED_MESSAGE:
        "The number of invoices you use exceeds the limit",
      INPUT_INVALID_MESSAGE: "INPUT_DATA_IS_INVALID"
    };
  },
  methods: {
    ...mapActions(["createNewInvoiceByForm"]),

    /**
     * Gom lỗi phát hành: ưu tiên LICENSE; nếu API có message/data thì truyền object cho modal.
     */
    rejectWithReleaseFailure(reject, error, uiCode) {
      if (
        error &&
        error.message_code &&
        error.message_code === this.LICENSE_EXPIRED_MESSAGE
      ) {
        reject(ERROR_CODES.LICENSE_EXPIRED);
        return;
      }
      const payload = resolveReleaseErrorPayload(
        error,
        uiCode,
        key => this.$t(key)
      );
      reject(payload || uiCode);
    },
    handleErrorCreate(error, reject) {
      if (error && error.message_code) {
        if (error.message_code === this.INPUT_INVALID_MESSAGE) {
          if (Array.isArray(error.data) && error.data.length === 0) {
            reject({
              message_code: ERROR_CODES.CREATE_TMP_INVOICE_FAILED,
              message: error.message
            });
          } else if (typeof error.data === "object" && error.data !== null) {
            reject(error.data);
          }
        } else if (error.message_code === this.LICENSE_EXPIRED_MESSAGE) {
          reject(ERROR_CODES.LICENSE_EXPIRED);
        } else {
          const payload = resolveReleaseErrorPayload(
            error,
            ERROR_CODES.CREATE_TMP_INVOICE_FAILED,
            key => this.$t(key)
          );
          reject(payload || ERROR_CODES.CREATE_TMP_INVOICE_FAILED);
        }
      } else {
        reject(ERROR_CODES.CREATE_TMP_INVOICE_FAILED);
      }
    },

    createAndReleaseInvoice(payload) {
      const vm = this;
      return new Promise((resolve, reject) => {
        new CaSignProxy().listHSM().then(caResponse => {
          if (hasHsm(caResponse)) {
            // Ký HSM
            payload.ca_type = caResponse[0].ca_type;
            payload.ca_id = caResponse[0].id;
            vm.createNewInvoiceByForm(payload)
              .then(response => {
                resolve(response);
              })
              .catch(error => {
                this.handleErrorCreate(error, reject);
              });
          } else {
            // Ký USB
            getCurrentUsbInfo().then(
              () => {
                const result = vm.createNewInvoiceByForm(payload);
                result
                  .then(response => {
                    const invoiceId = response.id;
                    const pdfPayload = {
                      // PDF
                      pdf_url: response.unsigned_pdf,
                      sign_pos: `${parseInt(
                        response.location_sign_left,
                        0
                      )}x${parseInt(response.location_sign_bottom, 0)}`,
                      reason: "sign contract",
                      page_number: response.location_page
                        ? response.location_page
                        : 1
                    };
                    const xlmPayload = {
                      // XML
                      xml_url: response.unsigned_xml,
                      sign_pos: `${parseInt(
                        response.location_sign_left,
                        0
                      )}x${parseInt(response.location_sign_bottom, 0)}`,
                      reason: "sign contract",
                      tag_path: response.unsigned_xml.includes("_mmt.xml")
                        ? "TDiep/CKSNNT"
                        : "HDon/DSCKS/NBan",
                      uu_id: response.uu_id
                    };
                    const pTokenProxy = new PTokenProxy();
                    Promise.all([
                      pTokenProxy.signXml(xlmPayload),
                      pTokenProxy.signPdf(pdfPayload)
                    ]).then(
                      responses => {
                        const signedPdf = new File([responses[1]], "file.pdf", {
                          type: "application/pdf"
                        });

                        const signedXml = new File([responses[0]], "file.xml", {
                          type: "application/pdf"
                        });
                        // Phát hành hóa đơn.
                        new UnpublishInvoiceProxy()
                          .releaseUnpublishInvoice(
                            {
                              id: invoiceId,
                              pdf: signedPdf,
                              xml: signedXml
                            },
                            {}
                          )
                          .then(
                            () => {
                              resolve(response);
                            },
                            error => {
                              this.rejectWithReleaseFailure(
                                reject,
                                error,
                                ERROR_CODES.RELEASE_INVOICE_FAILED
                              );
                            }
                          );
                      },
                      () => {
                        reject(ERROR_CODES.SIGN_INVOICE_FAILED);
                      }
                    );
                  })
                  .catch(error => {
                    this.handleErrorCreate(error, reject);
                  });
              },
              errorCode => {
                reject(errorCode);
              }
            );
          }
        });
      });
    },

    handleInvoice(payload, promiseCallback) {
      return new Promise((resolve, reject) => {
        new CaSignProxy().listHSM().then(caResponse => {
          if (hasHsm(caResponse)) {
            // Ký HSM
            if (payload.id_pinvoice) {
              payload.ca_type = caResponse[0].ca_type;
              payload.ca_id = caResponse[0].id;
            } else {
              payload.change_data.ca_type = caResponse[0].ca_type;
              payload.change_data.ca_id = caResponse[0].id;
            }
            promiseCallback(payload)
              .then(response => {
                const invoiceId = response.id_pinvoice
                  ? response.id_pinvoice
                  : response.id;
                new EInvoiceProxy()
                  .releaseReplaceModifyPublishedInvoice(
                    {
                      id: invoiceId,
                      ca_type: caResponse[0].ca_type,
                      ca_id: caResponse[0].id
                    },
                    {}
                  )
                  .then(
                    () => {
                      resolve(response);
                    },
                    error => {
                      this.rejectWithReleaseFailure(
                        reject,
                        error,
                        ERROR_CODES.RELEASE_INVOICE_FAILED
                      );
                    }
                  );
              })
              .catch(error => {
                this.handleErrorCreate(error, reject);
              });
          } else {
            // Ký USB
            getCurrentUsbInfo().then(
              () => {
                const result = promiseCallback(payload);
                result
                  .then(response => {
                    const invoiceId = response.id_pinvoice
                      ? response.id_pinvoice
                      : response.id;
                    const pdfPayload = {
                      // PDF
                      pdf_url: response.converted_pdf,
                      sign_pos: `${parseInt(
                        response.location_sign_left,
                        0
                      )}x${parseInt(response.location_sign_bottom, 0)}`,
                      reason: "sign contract",
                      page_number: response.location_page
                        ? response.location_page
                        : 1
                    };
                    const xlmPayload = {
                      // XML
                      xml_url: response.signed_xml_url,
                      sign_pos: `${parseInt(
                        response.location_sign_left,
                        0
                      )}x${parseInt(response.location_sign_bottom, 0)}`,
                      reason: "sign contract",
                      tag_path: response.signed_xml_url.includes("_mmt.xml")
                        ? "TDiep/CKSNNT"
                        : "HDon/DSCKS/NBan",
                      uu_id: response.uu_id
                    };
                    const pTokenProxy = new PTokenProxy();
                    Promise.all([
                      pTokenProxy.signXml(xlmPayload),
                      pTokenProxy.signPdf(pdfPayload)
                    ]).then(
                      responses => {
                        const signedPdf = new File([responses[1]], "file.pdf", {
                          type: "application/pdf"
                        });

                        const signedXml = new File([responses[0]], "file.xml", {
                          type: "application/pdf"
                        });
                        // Phát hành hóa đơn.
                        new EInvoiceProxy()
                          .releaseReplaceModifyPublishedInvoice(
                            {
                              id: invoiceId,
                              pdf: signedPdf,
                              xml: signedXml
                            },
                            {}
                          )
                          .then(
                            () => {
                              resolve(response);
                            },
                            error => {
                              this.rejectWithReleaseFailure(
                                reject,
                                error,
                                ERROR_CODES.RELEASE_INVOICE_FAILED
                              );
                            }
                          );
                      },
                      () => {
                        reject(ERROR_CODES.SIGN_INVOICE_FAILED);
                      }
                    );
                  })
                  .catch(error => {
                    this.handleErrorCreate(error, reject);
                  });
              },
              errorCode => {
                reject(errorCode);
              }
            );
          }
        });
      });
    },

    exportInvoiceChange(id) {
      return new Promise((resolve, reject) => {
        new CaSignProxy().listHSM().then(caResponse => {
          if (hasHsm(caResponse)) {
            new PublishedInvoiceProxy()
              .getTransferPublishedInvoice(id)
              .then(response => {
                const urlExport =
                  DOWNLOAD_URL + encodeURIComponent(response.tranfered_pdf);
                window.open(urlExport, "_blank");
                resolve(response);
              })
              .catch(() => {
                reject(ERROR_CODES.TRANSFER_INVOICE_FAILED);
              });
          } else {
            getCurrentUsbInfo().then(
              () => {
                new PublishedInvoiceProxy()
                  .prepareTransferInvoiceForToken(id)
                  .then(
                    response => {
                      const pdfPayload = {
                        // PDF
                        pdf_url: response.tranfered_pdf,
                        sign_pos: `${parseInt(
                          response.location_sign_left,
                          0
                        )}x${parseInt(response.location_sign_bottom, 0)}`,
                        reason: "sign contract",
                        page_number: response.location_page
                          ? response.location_page
                          : 1
                      };

                      new PTokenProxy().signPdf(pdfPayload).then(
                        res => {
                          const signedPdf = new File([res], "file.pdf", {
                            type: "application/pdf"
                          });

                          const dataSubmit = new FormData();
                          dataSubmit.append("tranfered_file", signedPdf);

                          new PublishedInvoiceProxy()
                            .transferInvoiceForToken({ id, data: dataSubmit })
                            .then(
                              r => {
                                const urlExport =
                                  DOWNLOAD_URL +
                                  encodeURIComponent(r.tranfered_pdf);
                                window.open(urlExport, "_blank");
                                resolve(r);
                              },
                              () => {
                                reject(ERROR_CODES.TRANSFER_INVOICE_FAILED);
                              }
                            );
                        },
                        () => {
                          reject(ERROR_CODES.SIGN_INVOICE_FAILED);
                        }
                      );
                    },
                    error => {
                      this.handleErrorCreate(error, reject);
                    }
                  );
              },
              errorCode => {
                reject(errorCode);
              }
            );
          }
        });
      });
    },

    handleDraftInvoice(payload) {
      const { id_pinvoice: idPInvoice, ...data } = payload;
      return new Promise((resolve, reject) => {
        if (idPInvoice) {
          new EInvoiceProxy().updateEInvoice({ ...data, id: idPInvoice }).then(
            response => {
              resolve(response);
            },
            () => {
              new EInvoiceProxy().createEInvoice(payload).then(
                response => {
                  resolve(response);
                },
                error => {
                  reject(error);
                }
              );
            }
          );
        } else {
          new EInvoiceProxy().createEInvoice(payload).then(
            response => {
              resolve(response);
            },
            error => {
              reject(error);
            }
          );
        }
      });
    },

    releaseUnpublishInvoice(invoiceID) {
      return new Promise((resolve, reject) => {
        new CaSignProxy().listHSM().then(caResponse => {
          if (hasHsm(caResponse)) {
            const data = {
              id: invoiceID,
              ca_type: caResponse[0].ca_type,
              ca_id: caResponse[0].id
            };
            new UnpublishInvoiceProxy().releaseUnpublishInvoice(data, {}).then(
              response => {
                resolve(response);
              },
              error => {
                this.rejectWithReleaseFailure(
                  reject,
                  error,
                  ERROR_CODES.RELEASE_INVOICE_FAILED
                );
              }
            );
          } else {
            // Ký USB
            getCurrentUsbInfo().then(
              () => {
                const result = new UnpublishInvoiceProxy().prepareRelease(
                  invoiceID
                );
                result
                  .then(response => {
                    const pdfPayload = {
                      // PDF
                      pdf_url: response.converted_pdf,
                      sign_pos: `${parseInt(
                        response.location_sign_left,
                        0
                      )}x${parseInt(response.location_sign_bottom, 0)}`,
                      reason: "sign contract",
                      page_number: response.location_page
                        ? response.location_page
                        : 1
                    };
                    const xlmPayload = {
                      // XML
                      xml_url: response.signed_xml_url,
                      sign_pos: `${parseInt(
                        response.location_sign_left,
                        0
                      )}x${parseInt(response.location_sign_bottom, 0)}`,
                      reason: "sign contract",
                      tag_path: response.signed_xml_url.includes("_mmt.xml")
                        ? "TDiep/CKSNNT"
                        : "HDon/DSCKS/NBan",
                      uu_id: response.uu_id
                    };
                    const pTokenProxy = new PTokenProxy();
                    Promise.all([
                      pTokenProxy.signXml(xlmPayload),
                      pTokenProxy.signPdf(pdfPayload)
                    ]).then(
                      responses => {
                        const signedPdf = new File([responses[1]], "file.pdf", {
                          type: "application/pdf"
                        });

                        const signedXml = new File([responses[0]], "file.xml", {
                          type: "application/pdf"
                        });
                        // Phát hành hóa đơn.
                        new UnpublishInvoiceProxy()
                          .releaseUnpublishInvoice(
                            {
                              id: response.id,
                              pdf: signedPdf,
                              xml: signedXml
                            },
                            {}
                          )
                          .then(
                            () => {
                              resolve(response);
                            },
                            error => {
                              this.rejectWithReleaseFailure(
                                reject,
                                error,
                                ERROR_CODES.RELEASE_INVOICE_FAILED
                              );
                            }
                          );
                      },
                      () => {
                        reject(ERROR_CODES.SIGN_INVOICE_FAILED);
                      }
                    );
                  })
                  .catch(error => {
                    this.handleErrorCreate(error, reject);
                  });
              },
              errorCode => {
                reject(errorCode);
              }
            );
          }
        });
      });
    },

    reportInvoiceError(payload) {
      return new Promise((resolve, reject) => {
        new CaSignProxy().listHSM().then(caResponse => {
          if (hasHsm(caResponse)) {
            // Ký HSM
            payload.ca_type = caResponse[0].ca_type;
            payload.ca_id = caResponse[0].id;
            payload.export_type = "XML";

            new PublishedInvoiceProxy(payload)
              .statisticMistakeInvoice()
              .then(response => {
                resolve(response);
              })
              .catch(error => {
                this.rejectWithReleaseFailure(
                  reject,
                  error,
                  ERROR_CODES.REPORT_INVOICE_ERROR_FAILED
                );
              });
          } else {
            // Ký USB
            payload.export_type = "TEXT";

            getCurrentUsbInfo().then(
              () => {
                const ThisPublishedInvoiceProxy = new PublishedInvoiceProxy(
                  payload
                );
                const result = ThisPublishedInvoiceProxy.statisticMistakeInvoice();
                result
                  .then(response => {
                    const pdfPayload = {
                      // PDF
                      pdf_url: response.url_pdf_file,
                      sign_pos: `${parseInt(response.left, 0)}x${parseInt(
                        response.bottom,
                        0
                      )}`,
                      reason: "sign contract",
                      page_number: response.page_number
                        ? response.page_number
                        : 1
                    };
                    const xlmPayload = {
                      // XML
                      xml_url: response.url_file,
                      sign_pos: `${parseInt(response.left, 0)}x${parseInt(
                        response.bottom,
                        0
                      )}`,
                      reason: "sign contract",
                      tag_path: response.tag_path,
                      uu_id: response.uu_id
                    };
                    const pTokenProxy = new PTokenProxy();
                    Promise.all([
                      pTokenProxy.signXml(xlmPayload),
                      pTokenProxy.signPdf(pdfPayload)
                    ]).then(
                      responses => {
                        const signedPdf = new File([responses[1]], "file.pdf", {
                          type: "application/pdf"
                        });

                        const signedXml = new File([responses[0]], "file.xml", {
                          type: "application/pdf"
                        });
                        // Phát hành hóa đơn. chưa check
                        ThisPublishedInvoiceProxy.sendStatisticMistakeInvoice(
                          {
                            id: response.uu_id,
                            pdf: signedPdf,
                            xml: signedXml
                          },
                          {}
                        ).then(
                          () => {
                            resolve(response);
                          },
                          error => {
                            this.rejectWithReleaseFailure(
                              reject,
                              error,
                              ERROR_CODES.REPORT_INVOICE_ERROR_FAILED
                            );
                          }
                        );
                      },
                      () => {
                        reject(ERROR_CODES.SIGN_INVOICE_FAILED);
                      }
                    );
                  })
                  .catch(error => {
                    this.rejectWithReleaseFailure(
                      reject,
                      error,
                      ERROR_CODES.REPORT_INVOICE_ERROR_FAILED
                    );
                  });
              },
              errorCode => {
                reject(errorCode);
              }
            );
          }
        });
      });
    }
  }
};

export default ReleaseInvoiceMixin;



// WEBPACK FOOTER //
// ./src/mixins/release-invoice.js